import { Text, View } from '@/components'
import { TypeGuards } from '@codeleap/common'
import { classifyVideoError } from './classifyVideoError'
import { useVideoContext } from './Immersive'

/**
 * The three shapes a video can fail in, with the copy for each. Only a stall or a network
 * failure is retryable — nothing is wrong with the video there. A video that isn't ready yet
 * or failed outright will fail the exact same way again immediately, so offering "tap to
 * retry" there is a promise the app can't keep.
 */
function getVideoErrorMessage(error: unknown, stalled: boolean) {
  const kind = classifyVideoError(error)

  if (TypeGuards.isNil(kind) && !stalled) return null

  if (TypeGuards.isNil(kind) || kind === 'network') {
    return {
      title: 'This is taking longer than usual',
      body: 'Check your connection and try again.',
      retryable: true
    }
  }

  if (kind === 'unavailable') {
    return {
      title: `This video isn't ready yet`,
      body: `It's still being processed. Check back in a little while.`,
      retryable: false
    }
  }

  return { title: `Sorry, this video can't be played`, body: undefined, retryable: false }
}

export const VideoError = () => {
  const { errorStore, stalled, retry } = useVideoContext()

  const error = errorStore.use(s => s)
  const hasError = !TypeGuards.isNil(error) || stalled

  const message = getVideoErrorMessage(error, stalled)

  if (!hasError || !message) return null

  return (
    <View
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: 16,
        paddingRight: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        cursor: message.retryable ? 'pointer' : 'default'
      }}
      onClick={message.retryable ? retry : undefined}
    >
      {/* 16px each side: the overlay above is padded, this fills what's left rather than
          shrinking to content. */}
      <View
        variants={['bg:neutral1', 'padding:2', 'center']}
        style={{ width: '100%', borderRadius: 24, flexDirection: 'column' }}
      >
        <Text text={message.title} variants={['h4', 'textCenter', 'color:neutral10']} />
        {message.body ? (
          <Text text={message.body} variants={['p2', 'textCenter', 'color:neutral10', 'marginTop:1']} />
        ) : null}
        {message.retryable ? (
          <Text text="Tap to retry" variants={['p3', 'textCenter', 'color:neutral6', 'marginTop:2']} />
        ) : null}
      </View>
    </View>
  )
}
